import {Component, OnDestroy, OnInit} from '@angular/core';
import {BaseComponent} from '../../../core/component/base.component';

import {AlertService} from '../../../core/service/alert.service';
import {MessageService} from '../../../core/service/message.service';
import {Router} from '@angular/router';
import {SecurityService} from '../../../core/service/security.service';
import {LoggerService} from '../../../service/logger.service';

import {PayrollService} from '../../service/payroll.service';
import {Payroll} from '../../service/resource/payroll';
import {EmployeeService} from '../../service/employee.service';
import {Employee} from '../../service/resource/employee';
import {LocalDate} from '@js-joda/core';
import {Periodicity} from '../../service/resource/salary';
import {PayrollFilter} from '../../service/filter/payroll.filter';
import ServiceUtils from '../../../core/utils/service.utils';
import {Tenant} from '../../../core/model/tenant';

@Component({
    selector: 'app-payroll',
    templateUrl: './payroll.component.html',
    standalone: false
})
export class PayrollComponent extends BaseComponent<Payroll> implements OnInit, OnDestroy {

  employees: Employee[] = [];
  selectedDate: LocalDate = LocalDate.now();
  selectedPeriodicity: Periodicity = Periodicity.MONTHLY;

  from: LocalDate = LocalDate.now();
  to: LocalDate = LocalDate.now();
  employeeId?: number = -1;
  // Aguinaldo report dialog state
  aguinaldoEmployeeId: number | null = null;

  constructor(private payrollService: PayrollService,
              private alertService: AlertService,
              protected messageService: MessageService,
              protected loggerService: LoggerService,
              protected router: Router,
              protected securityService: SecurityService,
              protected employeeService: EmployeeService) {
    super(payrollService, 'Payroll', alertService, router, securityService, messageService, loggerService);
    this.sortBy = ['date', 'employeeName'];
    this.sortOrder = 'ASC';
    this.pageSize = 25;
    this.from = LocalDate.now().withDayOfMonth(1);
    this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
  }

  getModelInstance(): Payroll {
    return new Payroll();
  }

  clearFormErrors(): Map<string, string> {
    return new Map<string, string>();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.employeeService.getAll().subscribe({
        next: (v) => this.employees = v,
        error: (e) => this.processError(e)
      }
    );
  }

  generateEvent(selectedDate: LocalDate, periodicity: Periodicity): void {
    this.payrollService.generate(selectedDate, periodicity).subscribe(
      {
        complete: () => this.refreshEvent(),
        error: (e) => this.processError(e)
      }
    );
  }

  applyEvent(): void {
    this.loading = true;
    this.payrollService.apply(this.selectedDate).subscribe(
      {
        complete: () => this.refreshEvent(),
        error: (e) => this.processError(e)
      }
    );
  }

  sendEmailEvent(): void {
    this.loading = true;
    const payrollIds: number[] = [];
    if (this.model.id !== undefined) {
      payrollIds.push(this.model.id);
    }
    this.payrollService.sendEmail(payrollIds).subscribe(
      {
        complete: () => this.refreshEvent(),
        error: (e) => this.processError(e)
      }
    );
  }

  getPageEvent(pageNumber: number, refresh: boolean = false): void {
    const filter = new PayrollFilter();
    filter.fromDate = this.from;
    filter.toDate = this.to;
    if (this.employeeId !== undefined && this.employeeId !== -1) {
      filter.employeeId = this.employeeId;
    }
    this.loading = true;
    this.service.searchByFilter(filter, pageNumber, this.pageSize, this.sortBy, this.sortOrder)
      .subscribe({
        next: value => {
          this.page = value;
          this.loading = false;
        },
        error: err => this.processError(err)
      });
  }

    // refactored method and extracted helpers within PayrollComponent
    exportPayroll(): void {
        this.loading = true;
        const filter = this.buildPayrollFilter();

        this.payrollService.export(filter).subscribe({
            next: (blob: Blob) => {
                const fileName = this.getExportFileName();
                this.triggerBlobDownload(blob, fileName);
                this.loading = false;
            },
            error: (error) => this.processError(error)
        });
    }

    private buildPayrollFilter(): PayrollFilter {
        const filter = new PayrollFilter();
        filter.fromDate = this.from;
        filter.toDate = this.to;
        if (this.employeeId !== undefined && this.employeeId !== -1) {
            filter.employeeId = this.employeeId;
        }
        return filter;
    }

    private getExportFileName(): string {
        const tenant = ServiceUtils.getTenant() as Tenant | {};
        const name = (tenant as Tenant)?.name;
        const safeName = name ? name.replace(/\./g, '_') : 'Reporte_de_pagos';
        return name ? `Reporte_de_pagos_${safeName}` : safeName;
    }

    // Opens the Aguinaldo report dialog and initializes default values
    openAguinaldoReportDialog(): void {
        this.aguinaldoEmployeeId = null;
    }

    // Generates Aguinaldo report for the selected employee
    generateAguinaldoReport(): void {
        if (!this.aguinaldoEmployeeId) {
            return;
        }
        this.loading = true;
        this.payrollService.aguinaldoExport(this.aguinaldoEmployeeId).subscribe({
            next: (blob: Blob) => {
                const fileName = `Boleta_Aguinaldo_${this.aguinaldoEmployeeId}.xlsx`;
                this.triggerBlobDownload(blob, fileName);
                this.loading = false;
            },
            error: (error) => this.processError(error)
        });
    }


  onFromChange(): void {
    if (this.from && this.to) {
      if (this.from.isAfter(this.to)) {
        this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
      }
    } else if (this.from && !this.to) {
      this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
    }
    this.refreshEvent();
  }

  onToChange(): void {
    if (this.from && this.to) {
      if (this.to.isBefore(this.from)) {
        this.from = this.to.withDayOfMonth(1);
      }
    } else if (this.to && !this.from) {
      this.from = this.to.withDayOfMonth(1);
    }
    this.refreshEvent();
  }

  protected readonly Periodicity = Periodicity;
}
